import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { IconSnackBarService } from '@core/services/icon-snack-bar.service';
import { AssetsService } from '@app/pages/cmms/services/assets.service';
import { EMPTY, exhaustMap, mergeMap, of, switchMap } from 'rxjs';
import { catchError, debounceTime, map, take, tap, withLatestFrom } from 'rxjs/operators';
import { AuErrorAdapter } from '@app/shared';
import {
	addCmmsAsset,
	cmmsAssetsFailedToLoad,
	cmmsAssetsLoaded,
	createCmmsAssetError,
	createCmmsAssetSuccess,
	deleteCmmsAssets,
	deletingCmmsAssetSuccess,
	linkingCmmsAssetSuccess,
	openAssetWorkspaceFromOutside,
	openAssetWorkspaceFromOutsideByGlobalId,
	openNewAssetWorkspaceFromOutside,
	reloadCmmsAssetsWithSavePagination,
	setCmmsAssetForEditing,
	startCreateCmmsAsset,
	startDeletingCmmsAsset,
	startLinkingCmmsAsset,
	startLoadingCmmsAssets,
	startUpdateCmmsAsset,
	updateCmmsAsset,
	updateCmmsAssetSuccess,
	upsertCmmsAsset,
} from '@app/pages/cmms/components/assets/state/assets.actions';
import { AuRightSidebarService } from '@core/services/au-right-sidebar.service';
import { Store } from '@ngrx/store';
import {
	assetEntities,
	assetsQueryVariables,
	editedAsset,
} from '@app/pages/cmms/components/assets/state/assets.selectors';
import { AuErrorService } from '@core/services/au-error-service';
import { ICmmsState } from '@app/pages/cmms/state/cmms.reducer';
import { ICmmsAsset } from '@app/pages/cmms/interfaces/assets.interfaces';
import { defaultPageSize } from '@app/pages/cmms/configs/pagination-query-variables';
import { TranslateService } from '@tolgee/ngx';

@Injectable()
export class AssetsEffects {
	startLoadingCmmsAssets$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(startLoadingCmmsAssets),
			map(({ variables }) => {
				if (variables?.first || variables?.last) {
					return variables;
				}
				return {
					...variables,
					first: defaultPageSize,
				};
			}),
			switchMap(variables => {
				return this.assetsService.loadCmmsAssets(variables).pipe(
					map(({ assetPagination, assets }) => {
						return cmmsAssetsLoaded({ assetPagination, assets });
					}),
					catchError(error => {
						this.notify.error(AuErrorAdapter.getTextFromGqlError(error));
						return of(cmmsAssetsFailedToLoad());
					})
				);
			})
		);
	});
	reloadCmmsAssetsWithSavePagination$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(reloadCmmsAssetsWithSavePagination),
			withLatestFrom(this.store$.select(assetsQueryVariables)),
			map(([_, variables]) => {
				return startLoadingCmmsAssets({ variables });
			})
		);
	});
	startCreateCmmsAsset$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(startCreateCmmsAsset),
			exhaustMap(({ assets, entityToAttach, skipTableReload }) => {
				return this.assetsService.createAssetWithAttach(assets[0], entityToAttach).pipe(
					map(assets => {
						this.sidebarService.closeAndClearPortal();
						return createCmmsAssetSuccess({ skipTableReload, assets: [assets['asset']] });
					}),
					catchError(error => of(createCmmsAssetError({ error })))
				);
			})
		);
	});
	createCmmsAssetSuccess$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(createCmmsAssetSuccess),
			map(assetData => {
				this.notify.success(
					`${this.translateService.instant('asset')} ${this.translateService.instant('has_been_created_successfully')}`
				);
				return assetData.skipTableReload
					? addCmmsAsset({ asset: assetData.assets[0] })
					: reloadCmmsAssetsWithSavePagination();
			})
		);
	});
	createCmmsAssetError$ = createEffect(
		() => {
			return this.actions$.pipe(
				ofType(createCmmsAssetError),
				map(({ error }) => {
					if (error) {
						this.errorService.notifyUserAboutError({
							error: error,
							defaultMessage: this.translateService.instant('error_generic_try_again'),
						});
					}
					this.notify.error(this.translateService.instant('error_generic_try_again'));
				})
			);
		},
		{ dispatch: false }
	);
	startUpdateCmmsAsset$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(startUpdateCmmsAsset),
			debounceTime(200),
			switchMap(({ asset, skipTableReload }) => {
				return this.assetsService.editAsset(asset).pipe(
					map((payload: any) => {
						this.notify.success(
							`${this.translateService.instant('asset')} ${this.translateService.instant('has_been_updated_successfully')}`
						);
						return updateCmmsAssetSuccess({
							asset: payload.data.editAsset.asset,
							skipTableReload,
						});
					}),
					catchError(error => {
						this.notify.error(this.translateService.instant('error_generic_try_again'));
						return EMPTY;
					})
				);
			})
		);
	});
	updateCmmsAssetSuccess = createEffect(() => {
		return this.actions$.pipe(
			ofType(updateCmmsAssetSuccess),
			map(({ asset, skipTableReload }) => {
				this.store$.dispatch(setCmmsAssetForEditing({ assetEdit: asset }));

				if (!skipTableReload) {
					this.store$.dispatch(reloadCmmsAssetsWithSavePagination());
				}

				return updateCmmsAsset({
					update: {
						// this is id of entity adapter!!! this code is correct;
						id: asset.pk,
						changes: asset,
					},
				});
			})
		);
	});

	startDeletingCmmsAsset$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(startDeletingCmmsAsset),
			mergeMap(({ pk, skipTableReload }) => {
				return this.assetsService.deleteAsset(pk).pipe(
					map(() => {
						this.sidebarService.close();
						return deletingCmmsAssetSuccess({ pk, skipTableReload });
					}),
					catchError(error => {
						this.notify.error(AuErrorAdapter.getTextFromGqlError(error));
						return EMPTY;
					})
				);
			})
		);
	});

	deletingCmmsAssetSuccess$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(deletingCmmsAssetSuccess),
			switchMap(({ skipTableReload }) => {
				this.notify.success(this.translateService.instant('success_assets_delete'));

				return skipTableReload ? [] : [reloadCmmsAssetsWithSavePagination()];
			})
		);
	});

	deleteMultipleAssets$ = createEffect((): any => {
		return this.actions$.pipe(
			ofType(deleteCmmsAssets),
			withLatestFrom(this.store$.select(assetsQueryVariables)),
			mergeMap(([{ pks }, variables]) => {
				return this.assetsService.deleteAssets(pks).pipe(
					map(() => {
						this.notify.success(
							this.translateService.instant('assets_deleted', { count: pks?.length })
						);
						return startLoadingCmmsAssets({ variables });
					}),
					catchError(() => {
						this.notify.error(this.translateService.instant('error_generic_try_again'));
						return EMPTY;
					})
				);
			})
		);
	});

	openAssetWorkspaceFromOutside$ = createEffect(
		() => {
			return this.actions$.pipe(
				ofType(openAssetWorkspaceFromOutside),
				withLatestFrom(this.store$.select(assetEntities)),
				exhaustMap(([{ pk }, assets]) => {
					if (assets[pk]) {
						return of(assets[pk]);
					}

					return this.assetsService.getAssetByPk(pk);
				}),
				tap((asset: ICmmsAsset) => {
					if (!asset) {
						this.notify.error(this.translateService.instant('error_object_not_exist'));
					} else {
						this.assetsService.viewAssetInWorkspace(asset);
					}
				}),

				catchError(error => {
					this.notify.error(AuErrorAdapter.getTextFromGqlError(error));
					return EMPTY;
				})
			);
		},
		{ dispatch: false }
	);
	openAssetWorkspaceFromOutsideByGlobalId$ = createEffect(
		() => {
			return this.actions$.pipe(
				// GlobalId(globalIfcId) is a IFC id of 3d element
				// We are extracting assets from uploaded ifc files
				// And the only way to find asset clicking by 3d element from ifc file - is to use globalIFcId
				// Therefore this is impossible to find asset by pk in such case
				ofType(openAssetWorkspaceFromOutsideByGlobalId),
				withLatestFrom(this.store$.select(assetEntities)),
				switchMap(([{ globalId }, assets]) => {
					const assetFromCache = Object.values(assets).find(
						asset => asset.ifcId.globalId === globalId
					);

					if (assetFromCache) {
						this.processAsset(assetFromCache);
						return of(assetFromCache);
					} else {
						return this.assetsService.getAssetByGlobalId(globalId).pipe(
							tap(asset => {
								if (asset) {
									this.store$.dispatch(upsertCmmsAsset({ asset }));
									this.processAsset(asset);
								} else {
									this.notify.error(
										this.translateService.instant('error_asset_type_not_exist')
									);
								}
							})
						);
					}
				}),
				catchError(error => {
					this.notify.error(AuErrorAdapter.getTextFromGqlError(error));
					return EMPTY;
				})
			);
		},
		{ dispatch: false }
	);

	private processAsset(asset) {
		if (!asset) {
			this.notify.error(this.translateService.instant('error_generic_try_again'));
		} else {
			this.assetsService.viewAssetInWorkspace(asset);
		}
	}

	openNewAssetWorkspaceFromOutside$ = createEffect(
		() => {
			return this.actions$.pipe(
				ofType(openNewAssetWorkspaceFromOutside),
				map(() => {
					return this.assetsService.openNewAssetWorkspace();
				})
			);
		},
		{ dispatch: false }
	);

	startLinkingCmmsAsset$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(startLinkingCmmsAsset),
			exhaustMap(linkAssetsProperties => {
				return this.assetsService.linkAssetsObjectsVariables(linkAssetsProperties).pipe(
					map(() => {
						this.notify.success(
							`${this.translateService.instant('asset')} ${this.translateService.instant('has_been_updated_successfully')}`
						);

						// TODO This is shitty code. Need to think about refactoring.
						// Thanks for that linking mutation can't return asset after success
						// and the same time it overload all values that we send
						this.store$
							.select(editedAsset)
							.pipe(take(1))
							.subscribe(editedAsset => {
								this.store$.dispatch(setCmmsAssetForEditing({ assetEdit: editedAsset }));
							});
						const assetId = linkAssetsProperties.assets[0];
						return linkingCmmsAssetSuccess({ assetId: assetId });
					}),
					catchError(error => {
						this.notify.error(AuErrorAdapter.getTextFromGqlError(error));
						return EMPTY;
					})
				);
			})
		);
	});

	constructor(
		private actions$: Actions,
		private notify: IconSnackBarService,
		private assetsService: AssetsService,
		private errorService: AuErrorService,
		private sidebarService: AuRightSidebarService,
		private store$: Store<ICmmsState>,
		private translateService: TranslateService
	) {}
}
